/** @format */

import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  _$,
  Card,
  Button,
  Table,
  TableColumn,
  Pagination,
  Icon,
  Checkbox,
  Select,
  Option,
  Dialog
} from '@king-design/react'
import MonitorCrumb from 'src/components/MonitorCrumb'
import { MonitorSearch, QuickRangeDate } from 'src/components'
import { EDIT_GRAPH_CHART_QUICK_RANGE } from 'src/components/QuickRangeDate'
import Highlighter from 'react-highlight-words'
import dayjs from 'dayjs'
import { useHistory, useLocation } from 'react-router'
import { getRecordList } from 'src/api/ApiEventOpsRecord'
import { useAsyncFn } from 'react-use'
import ReactJson from 'react-json-view'
import { StatusEnum, OriginEnum, EvaluationEnum, getStatus } from './container'
import { handleExportForm } from 'src/utils'
import { initPage, PageProps } from 'src/utils/interface'

interface SearchProps extends PageProps {
  Search: string
  Result: string
  Status: '' | -1 | 2 | 3
  StartedAt: [number, number] | []
  PrePublish: boolean
}
const initSearch: SearchProps = {
  ...initPage,
  Search: '',
  Result: '',
  Status: '',
  StartedAt: [],
  PrePublish: false
}
function useQuery() {
  return new URLSearchParams(useLocation().search)
}

const allColumns = [
  {
    label: _$('编号'),
    value: 'ID'
  },
  {
    label: _$('来源'),
    value: 'Origin'
  },
  {
    label: _$('名称'),
    value: 'Name'
  },
  {
    label: _$('发起时间'),
    value: 'StartedAt'
  },
  {
    label: _$('耗时'),
    value: 'Time'
  },
  {
    label: _$('结果'),
    value: 'Result'
  },
  {
    label: _$('输入参数'),
    value: 'Original'
  },
  {
    label: _$('是否正确'),
    value: 'Evaluation'
  },
  {
    label: _$('状态'),
    value: 'Status'
  }
]

type ColumnValue = typeof allColumns[number]['value']

const TriggerPage: React.FC<{ className?: string }> = ({ className }) => {
  const history = useHistory()
  const query = useQuery()
  const initPrePublish = query.get('prePublish') || 'false'
  const [search, setSearch] = useState<SearchProps>({
    ...initSearch,
    PrePublish: initPrePublish === 'true'
  })
  // 筛选列
  const storeCheckedColumns = localStorage.getItem('record-checked-columns')?.split(',')
  const initCheckedColumns: ColumnValue[] = storeCheckedColumns ?? [
    'ID',
    'Origin',
    'Name',
    'StartedAt',
    'Time',
    'Result',
    'Evaluation',
    'Status'
  ]
  const [columnSetting, setColumnSetting] = useState<boolean>(false)
  const [checkedColumns, setCheckedColumns] = useState<ColumnValue[]>(initCheckedColumns)
  const [checkedColumnValues, setCheckedColumnValues] = useState<ColumnValue[]>(initCheckedColumns)
  // 获取列表
  const [{ value: recordList }, fetchRecordList] = useAsyncFn(
    async (params?) => {
      const result = await getRecordList({
        ...params,
        Offset: (search.Page - 1) * search.PageSize,
        Limit: search.PageSize,
        Search: search.Search || undefined,
        Result: search.Result || undefined,
        Status: search.Status ? Number(search.Status) : undefined,
        StartedAtStart: search.StartedAt[0]
          ? dayjs(search.StartedAt[0]).format('YYYY-MM-DD HH:mm:ss')
          : undefined,
        StartedAtEnd: search.StartedAt[1]
          ? dayjs(search.StartedAt[1]).format('YYYY-MM-DD HH:mm:ss')
          : undefined,
        PrePublish: search.PrePublish
      })
      return result
    },
    [search]
  )

  // 导出执行记录
  const exportRecord = async () => {
    const result = await getRecordList({
      offset: 0,
      limit: 9999,
      Search: search.Search || undefined,
      Result: search.Result || undefined,
      Status: search.Status ? Number(search.Status) : undefined,
      StartedAtStart: search.StartedAt[0]
        ? dayjs(search.StartedAt[0]).format('YYYY-MM-DD HH:mm:ss')
        : undefined,
      StartedAtEnd: search.StartedAt[1]
        ? dayjs(search.StartedAt[1]).format('YYYY-MM-DD HH:mm:ss')
        : undefined,
      PrePublish: search.PrePublish
    })
    if (result?.Info?.length) {
      const columns = checkedColumns.map(item => {
        const findKey = allColumns.find(column => column.value === item)
        return findKey
      })
      const data = result.Info?.map(item => {
        return columns.map(column => {
          const text = item[column.value]
          if (column.value === 'Origin') {
            return OriginEnum[text] || '-'
          } else if (column.value === 'StartedAt') {
            return text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-'
          } else if (column.value === 'Time') {
            return item.StartedAt && item.FinishedAt
              ? dayjs(item.FinishedAt).diff(dayjs(item.StartedAt), 's')
              : '-'
          } else if (column.value === 'Original') {
            return text ? JSON.stringify(text) : '-'
          } else if (column.value === 'Result') {
            return text ? (Array.isArray(text) ? text.join(',') : text) : '-'
          } else if (column.value === 'Evaluation') {
            return EvaluationEnum[text] || '-'
          } else if (column.value === 'Status') {
            return StatusEnum[text]
          } else {
            return text || '-'
          }
        })
      })
      handleExportForm(
        columns.map(item => ({ key: item.value, value: item.label })),
        data,
        _$('时间运维执行记录')
      )
    }
  }

  useEffect(() => {
    fetchRecordList()
  }, [search])

  return (
    <div id="record-page" className={className}>
      <MonitorCrumb breadArr={[{ title: _$('自动化运维') }, { title: _$('执行记录') }]} />
      <Card style={{ marginBottom: 16 }}>
        <div className="header_search ksc-space">
          <div className="search_item">
            <p className="search_item_label">{_$('名称')}</p>
            <MonitorSearch
              placeholder={_$('请输入名称，支持模糊搜索')}
              onChange={value => {
                setSearch({
                  ...search,
                  Page: 1,
                  Search: value
                })
              }}
            />
          </div>
          <div className="search_item">
            <p className="search_item_label">{_$('发起时间')}</p>
            <QuickRangeDate
              realoadIcon={false}
              format="YYYY-MM-DD HH:mm"
              optionalDateRange={90}
              dateDiff={31}
              defautlValue={search.StartedAt}
              quickRange={EDIT_GRAPH_CHART_QUICK_RANGE}
              changeValue={date => {
                setSearch({
                  ...search,
                  Page: 1,
                  StartedAt: date
                })
              }}
            />
          </div>
          <div className="search_item">
            <p className="search_item_label">{_$('结果')}</p>
            <MonitorSearch
              placeholder={_$('请输入结果，支持模糊搜索')}
              onChange={value => {
                setSearch({
                  ...search,
                  Page: 1,
                  Result: value
                })
              }}
            />
          </div>
          <div className="search_item">
            <p className="search_item_label">{_$('状态')}</p>
            <Select
              value={search.Status}
              clearable
              width={120}
              onChangeValue={value => {
                setSearch({
                  ...search,
                  Status: value,
                  Page: 1
                })
              }}
            >
              <Option value="">{_$('全部')}</Option>
              {Object.entries(StatusEnum)?.map(([key, value]) => (
                <Option key={key} value={key}>
                  {value}
                </Option>
              ))}
            </Select>
          </div>
          <div className="search_item">
            <Checkbox
              value={search.PrePublish}
              onChangeValue={value => {
                history.push(`/monitor/eventops/record?prePublish=${value}`)
                setSearch({ ...search, PrePublish: !!value })
              }}
            >
              {_$('仅查看测试记录')}
            </Checkbox>
          </div>
        </div>
        <div className="header_btns ksc-space">
          <Button
            icon
            onClick={() => {
              setColumnSetting(true)
            }}
          >
            <Icon className="k-icon-settings" />
          </Button>
          <Button
            icon
            onClick={() => {
              exportRecord()
            }}
          >
            <Icon className="downLoad ion-android-download" />
          </Button>
          <Button icon onClick={() => fetchRecordList()}>
            <Icon className="ion-refresh" />
          </Button>
        </div>
        <Table
          data={recordList?.Info || []}
          animation={false}
          rowKey={record => record.id}
          checkType="none"
          onChangeSort={sort => {
            setSearch({
              ...search,
              Page: 1
            })
          }}
        >
          <TableColumn key="ID" title="ID" width="80" hidden={!checkedColumns.includes('ID')} />
          <TableColumn
            key="Origin"
            title={_$('来源')}
            width="80"
            hidden={!checkedColumns.includes('Origin')}
            slotTemplate={([{ Origin }]) => {
              return _$(OriginEnum[Origin]) || '-'
            }}
          />
          <TableColumn
            key="Name"
            title={_$('名称')}
            width="200"
            hidden={!checkedColumns.includes('Name')}
          />
          <TableColumn
            key="StartedAt"
            title={_$('发起时间')}
            width="100"
            hidden={!checkedColumns.includes('StartedAt')}
            slotTemplate={([{ StartedAt }]) => {
              return StartedAt ? dayjs(StartedAt).format('YYYY-MM-DD HH:mm:ss') : '-'
            }}
          />
          <TableColumn
            key="FinishedAt"
            title={_$('耗时')}
            width="100"
            hidden={!checkedColumns.includes('Time')}
            slotTemplate={([{ StartedAt, FinishedAt }]) => {
              return StartedAt && FinishedAt ? dayjs(FinishedAt).diff(dayjs(StartedAt), 's') : '-'
            }}
          />
          <TableColumn
            key="Original"
            title={_$('输入参数')}
            width="100"
            hidden={!checkedColumns.includes('Original')}
            slotTemplate={([{ Original }]) => {
              return Original ? (
                <ReactJson
                  src={Original ?? {}}
                  name={false}
                  displayDataTypes={false}
                  displayObjectSize={false}
                  quotesOnKeys={false}
                  enableClipboard={false}
                  collapsed={1}
                />
              ) : (
                '-'
              )
            }}
          />
          <TableColumn
            key="Result"
            title={_$('结果')}
            width="100"
            hidden={!checkedColumns.includes('Result')}
            slotTemplate={([{ Result }]) => {
              return (
                <Highlighter
                  highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                  searchWords={[search.Result]}
                  autoEscape
                  textToHighlight={Array.isArray(Result) ? Result.join(',') : Result}
                />
              )
            }}
          />
          <TableColumn
            key="Evaluation"
            title={_$('是否正确')}
            width="100"
            hidden={!checkedColumns.includes('Evaluation')}
            slotTemplate={([{ Evaluation }]) => {
              return _$(EvaluationEnum[Evaluation]) || '-'
            }}
          />
          <TableColumn
            key="Status"
            title={_$('状态')}
            width="100"
            hidden={!checkedColumns.includes('Status')}
            slotTemplate={([{ Status }]) => {
              return getStatus(Status)
            }}
          />
          <TableColumn
            key="Operations"
            title={_$('操作')}
            width="80"
            fixed="right"
            slotTemplate={([record]) => {
              return (
                <a
                  onClick={() => {
                    history.push(`/monitor/eventops/record/detail/${record.ID}`)
                  }}
                >
                  {_$('详情')}
                </a>
              )
            }}
          />
        </Table>
        <Pagination
          value={search.Page}
          limit={search.PageSize}
          total={recordList?.Total || 0}
          size="small"
          onChangeValue={value => {
            setSearch({
              ...search,
              Page: value
            })
          }}
          onChangeLimit={limit => {
            setSearch({
              ...search,
              PageSize: limit
            })
          }}
        />
      </Card>
      <Dialog
        value={columnSetting}
        width={650}
        title={_$('自定义表格显示项')}
        container={() => document.querySelector('#record-page')}
        ok={() => {
          setCheckedColumns(checkedColumnValues)
          setColumnSetting(false)
          localStorage.setItem('record-checked-columns', checkedColumnValues.join(','))
        }}
        onChangeValue={show => {
          setColumnSetting(show)
          if (!show) {
            setCheckedColumns(initCheckedColumns)
            setCheckedColumnValues(initCheckedColumns)
          }
        }}
      >
        <div className="ksc-space">
          {allColumns.map(item => {
            return (
              <Checkbox
                key={item.value}
                name="column"
                trueValue={item.value}
                value={checkedColumnValues}
                onChangeValue={value => {
                  setCheckedColumnValues(value)
                }}
              >
                {_$(item.label)}
              </Checkbox>
            )
          })}
        </div>
      </Dialog>
    </div>
  )
}

export default styled(TriggerPage)`
  padding: 0 16px;
  .header_search {
    .search_item {
      display: flex;
      align-items: center;
      margin-bottom: 8px;
      .search_item_label {
        margin: 0px 8px 0 0;
      }
    }
  }
  .header_btns {
    margin-bottom: 8px;
    justify-content: flex-end;
  }
  .k-pagination {
    margin-top: 16px;
    text-align: center;
  }
`
